/**
 * Per-tenant theme tokens — the data half of "one codebase, many client looks".
 *
 * Stored in `tenant_config.theme` (jsonb) and served by the public
 * `GET /tenant/config`. The SPA injects these as inline CSS custom properties
 * on <html> at boot (see `useTenantTheme` in the member app). Inline element
 * styles outrank ANY stylesheet rule, so a tenant theme overrides the baseline
 * CSS in `member-theme.css` / `@mlm/ui/styles.css` WITHOUT editing those files.
 *
 * Tokens a tenant omits fall through to the baseline stylesheet — so an
 * un-themed deployment is visually identical to the demo/showroom (Omni orange).
 *
 * Color values are HSL TRIPLETS ("H S% L%", e.g. "9.9 85% 55.5%") to match the
 * shadcn convention used throughout `@mlm/ui` (consumed as `hsl(var(--primary))`).
 * They are NOT hex. Non-color tokens (radius, fonts, gradients) are raw CSS values.
 *
 * Because the storefront's brand vars now live on `:root` (see Phase 1 of the
 * white-label plan), these tokens reskin BOTH the member portal and the public
 * storefront from a single row — no per-tenant code.
 */
export interface ThemeTokens {
  /** Brand color. HSL triplet, e.g. "9.9 85% 55.5%". */
  primary?: string;
  primaryForeground?: string;
  accent?: string;
  accentForeground?: string;
  ring?: string;
  background?: string;
  foreground?: string;
  card?: string;
  cardForeground?: string;
  border?: string;
  /** Corner radius, raw CSS, e.g. "0.5rem". */
  radius?: string;
  /**
   * Body font stack, raw CSS, e.g. "'Hanken Grotesk', system-ui, sans-serif".
   * @deprecated Back-compat alias — emits BOTH `--font-sans` and `--font-body`.
   * Prefer `fontBody` / `fontHeading`.
   */
  fontSans?: string;
  /** Brand logo URL (kept here so all branding lives in one object). */
  logoUrl?: string;

  // --- Full storefront surface set (HSL triplets) ---
  secondary?: string;
  secondaryForeground?: string;
  muted?: string;
  mutedForeground?: string;
  popover?: string;
  popoverForeground?: string;
  input?: string;
  success?: string;
  successForeground?: string;

  // --- Agency typography (raw CSS font stacks) ---
  /** Heading font stack, e.g. "'Playfair Display', serif". -> `--font-heading` */
  fontHeading?: string;
  /** Body font stack, e.g. "'Inter', system-ui, sans-serif". -> `--font-body` */
  fontBody?: string;
  /** Google-Fonts (or any) stylesheet hrefs injected at runtime as <link>. */
  fontLinks?: string[];

  // --- Promoted bespoke brand tokens (HSL triplets / raw CSS for gradients) ---
  /** Darker brand shade (hover states). HSL triplet. -> `--brand-dark` (drives `o-d`). */
  brandDark?: string;
  /** Light brand tint. HSL triplet. -> `--brand-light` (drives `o-l`). */
  brandLight?: string;
  /** Secondary accent / "gold" highlight. HSL triplet. -> `--highlight` (drives `gold`). */
  highlight?: string;
  highlightForeground?: string;
  /** MLM PV badge greens. HSL triplets. -> `--pv` / `--pv-bg` / `--pv-border`. */
  pv?: string;
  pvBg?: string;
  pvBorder?: string;
  /** Brand gradient (banners/badges), raw CSS. -> `--brand-gradient` (replaces O_GRAD). */
  brandGradient?: string;
  /** Member-badge gradient, raw CSS. -> `--member-gradient`. */
  memberGradient?: string;
  /** Official-store gold band gradient, raw CSS. -> `--gold-gradient`. */
  goldGradient?: string;

  // --- Branding-as-data ---
  /** Text wordmark fallback when no `logoUrl` is set. */
  wordmark?: string;
  /** Optional logo variant for dark surfaces (e.g. footer). */
  logoUrlDark?: string;

  // --- Uploaded brand assets (R2 object keys, served via GET /tenant/logo/:which) ---
  /** Uploaded light-surface logo key. Wins over `logoUrl` when set. */
  logoKey?: string;
  /** Uploaded dark-surface logo key. Wins over `logoUrlDark` when set. */
  logoKeyDark?: string;
  /** Uploaded favicon key (PNG/JPEG). Unset → falls back to `logoKey` → `logoUrl`. */
  faviconKey?: string;
}

/**
 * Maps each CSS-var-backed token to the custom property it drives. Tokens that
 * are NOT CSS vars (`logoUrl`, `logoUrlDark`, `wordmark`, `fontLinks`) are
 * intentionally absent — they're handled outside `themeToCssVars`.
 */
const TOKEN_TO_CSS_VAR: Partial<Record<keyof ThemeTokens, string>> = {
  primary: '--primary',
  primaryForeground: '--primary-foreground',
  accent: '--accent',
  accentForeground: '--accent-foreground',
  ring: '--ring',
  background: '--background',
  foreground: '--foreground',
  card: '--card',
  cardForeground: '--card-foreground',
  border: '--border',
  radius: '--radius',
  fontSans: '--font-sans',
  secondary: '--secondary',
  secondaryForeground: '--secondary-foreground',
  muted: '--muted',
  mutedForeground: '--muted-foreground',
  popover: '--popover',
  popoverForeground: '--popover-foreground',
  input: '--input',
  success: '--success',
  successForeground: '--success-foreground',
  fontHeading: '--font-heading',
  fontBody: '--font-body',
  brandDark: '--brand-dark',
  brandLight: '--brand-light',
  highlight: '--highlight',
  highlightForeground: '--highlight-foreground',
  pv: '--pv',
  pvBg: '--pv-bg',
  pvBorder: '--pv-border',
  brandGradient: '--brand-gradient',
  memberGradient: '--member-gradient',
  goldGradient: '--gold-gradient',
};

/**
 * Returns the `[cssVar, value]` pairs to set on <html>. Includes ONLY the
 * tokens the tenant actually specified, so omitted tokens fall through to the
 * baseline stylesheet (the demo look) untouched.
 *
 * `fontSans` is a back-compat alias: it emits BOTH `--font-sans` and (unless a
 * dedicated `fontBody` is given) `--font-body`, so the new Tailwind `font-sans`
 * family (which reads `--font-body`) honors a legacy `fontSans` value.
 *
 * `fontLinks` is NOT emitted here — it is a list of stylesheet hrefs the SPA
 * injects as <link> elements (see `useTenantTheme`).
 */
export function themeToCssVars(theme: ThemeTokens | null | undefined): Array<[string, string]> {
  if (!theme) return [];
  const out: Array<[string, string]> = [];
  for (const [key, cssVar] of Object.entries(TOKEN_TO_CSS_VAR)) {
    const value = theme[key as keyof ThemeTokens];
    if (typeof value === 'string' && value.trim() !== '') {
      out.push([cssVar as string, value]);
    }
  }
  // Back-compat: a legacy `fontSans` also feeds the new `--font-body` var.
  if (
    typeof theme.fontSans === 'string' &&
    theme.fontSans.trim() !== '' &&
    !(typeof theme.fontBody === 'string' && theme.fontBody.trim() !== '')
  ) {
    out.push(['--font-body', theme.fontSans]);
  }
  return out;
}

/**
 * The baseline (demo / showroom) theme — the current Omni orange look every
 * prospect sees. It's the starting point a new tenant theme overrides
 * token-by-token. Values mirror `apps/member/src/member-theme.css`.
 *
 * `primary` is the EXACT #EE4D2D triplet (`9.9 85% 55.5%`); the old
 * `10 85% 55%` rendered #ee4b2b, ~2 units off the intended brand orange.
 */
export const BASELINE_THEME_TOKENS: ThemeTokens = {
  primary: '9.9 85% 55.5%', // #EE4D2D
  primaryForeground: '0 0% 100%',
  accent: '16 100% 97%', // #FFF1ED-ish light tint
  accentForeground: '10 85% 45%',
  ring: '9.9 85% 55.5%',
  brandDark: '13 88.4% 44.1%', // #D4380D
  brandLight: '13.3 100% 96.5%', // #FFF1ED
  highlight: '42.4 100% 50%', // #FFB400 gold
  pv: '151.3 62.7% 29.4%', // #1c7a4d
  pvBg: '140 54.5% 95.7%', // #eefaf2
  pvBorder: '145.9 49.3% 85.3%', // #c7ecd7
  brandGradient: 'linear-gradient(120deg, #ff6633, #ee4d2d 55%, #d4380d)',
  memberGradient: 'linear-gradient(120deg, #7b2ff7, #b14bf4)',
  goldGradient: 'linear-gradient(120deg, #ffb400, #ff8a00)',
};

// ── hex ↔ HSL triplet ─────────────────────────────────────────────────────
// Moved here from the admin Layout & Theme page when the replicated-page
// builder gained its own colour pickers: both surfaces edit the same triplet
// convention, and two copies of a colour-space conversion is two chances to
// round differently. Pure functions, no DOM — they belong beside the tokens.

export function hexToHslTriplet(hex: string): string {
  let h = hex.replace('#', '');
  if (h.length === 3)
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let hue = 0;
  let s = 0;
  const l = (max + min) / 2;
  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    if (max === r) hue = ((g - b) / d) % 6;
    else if (max === g) hue = (b - r) / d + 2;
    else hue = (r - g) / d + 4;
    hue *= 60;
    if (hue < 0) hue += 360;
  }
  const round = (n: number) => Math.round(n * 10) / 10;
  return `${round(hue)} ${round(s * 100)}% ${round(l * 100)}%`;
}

export function hslTripletToHex(t: string): string {
  const m = t.trim().match(/^([\d.]+)\s+([\d.]+)%\s+([\d.]+)%$/);
  if (!m) return '#cccccc';
  const H = Number(m[1]);
  const S = Number(m[2]) / 100;
  const L = Number(m[3]) / 100;
  const C = (1 - Math.abs(2 * L - 1)) * S;
  const X = C * (1 - Math.abs(((H / 60) % 2) - 1));
  const mm = L - C / 2;
  let r = 0;
  let g = 0;
  let b = 0;
  if (H < 60) [r, g, b] = [C, X, 0];
  else if (H < 120) [r, g, b] = [X, C, 0];
  else if (H < 180) [r, g, b] = [0, C, X];
  else if (H < 240) [r, g, b] = [0, X, C];
  else if (H < 300) [r, g, b] = [X, 0, C];
  else [r, g, b] = [C, 0, X];
  const f = (v: number) =>
    Math.round((v + mm) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${f(r)}${f(g)}${f(b)}`;
}
